/**
 * Nigel Sylvester — Theme JS
 * Game embed, cart drawer, signup modal, video modal.
 */

(function () {
  "use strict";

  window.NS = window.NS || {};

  var GAME_URL = "https://react-tetris-cyan.vercel.app/?embed=1";
  var SIGNUP_STORAGE_KEY = "ns-signup-dismissed";
  var VIDEO_ANIM_MS = 500;
  var SIGNUP_AUTO_MS = 3000;
  var FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");
  var activeDialog = null;

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        return !!(
          el.offsetWidth ||
          el.offsetHeight ||
          el.getClientRects().length
        );
      });
  }

  function focusDialog(dialog, focusSelector) {
    if (!dialog) return;
    var target = focusSelector ? dialog.querySelector(focusSelector) : null;
    var focusable = getFocusable(dialog);
    if (!target) target = focusable[0] || dialog;
    if (!dialog.hasAttribute("tabindex")) dialog.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
  }

  function openDialog(dialog, options) {
    if (!dialog) return;
    activeDialog = {
      el: dialog,
      restoreTo:
        options && options.restoreTo
          ? options.restoreTo
          : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null,
    };
    dialog.setAttribute("aria-hidden", "false");
    document.body.classList.add("has-open-dialog");
    setTimeout(function () {
      focusDialog(dialog, options && options.focusSelector);
    }, 0);
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    dialog.setAttribute("aria-hidden", "true");
    if (activeDialog && activeDialog.el === dialog) {
      var restoreTo = activeDialog.restoreTo;
      activeDialog = null;
      document.body.classList.remove("has-open-dialog");
      if (restoreTo && document.contains(restoreTo)) {
        restoreTo.focus({ preventScroll: true });
      }
    }
  }

  function maintainDialogFocus(e) {
    if (!activeDialog || e.key !== "Tab") return;
    var focusable = getFocusable(activeDialog.el);
    if (!focusable.length) {
      e.preventDefault();
      activeDialog.el.focus({ preventScroll: true });
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // --- Game modal ---
  var gModal, gBackdrop, gIframe, gLoader, gContainer;
  var isGameOpen = false;

  function getGameEls() {
    if (gModal) return;
    gModal = document.getElementById("game-modal");
    gBackdrop = document.getElementById("game-modal-backdrop");
    gIframe = document.getElementById("game-iframe");
    gLoader = document.getElementById("game-modal-loader");
    gContainer = document.getElementById("game-modal-container");
  }

  function showGameLoader(show) {
    getGameEls();
    if (gLoader) gLoader.style.display = show ? "flex" : "none";
  }

  NS.openGame = function () {
    getGameEls();
    if (!gModal || !gIframe || isGameOpen) return;
    isGameOpen = true;
    showGameLoader(true);
    gIframe.src = GAME_URL;
    gModal.classList.add("is-open");
    document.body.classList.add("is-game-mode");
    openDialog(gModal, { focusSelector: ".exit-overlay" });
  };

  NS.closeGame = function () {
    getGameEls();
    if (!gModal || !isGameOpen) return;
    isGameOpen = false;
    gModal.classList.remove("is-open");
    document.body.classList.remove("is-game-mode");
    if (gIframe) gIframe.src = "";
    showGameLoader(true);
    closeDialog(gModal);
    if (NS.resumeCarouselVideos) NS.resumeCarouselVideos();
  };

  window.closeGameModal = function () {
    NS.closeGame();
  };

  window.addEventListener("message", function (e) {
    if (!e || !e.data) return;
    var d = e.data;
    if (typeof d === "string") {
      try {
        d = JSON.parse(d);
      } catch (err) {
        if (d === "tetris-close" || d.indexOf("tetris") !== -1) NS.closeGame();
        return;
      }
    }
    if (d && (d.type === "tetris-ready" || d.tetris === "ready")) {
      showGameLoader(false);
    }
    if (d && (d.type === "tetris-close" || d.action === "close")) {
      NS.closeGame();
    }
  });

  // --- Cart drawer ---
  var cDrawer, cBackdrop, cBody, cFooter, cSubtotal, cStatus;
  var isCartOpen = false;

  function getCartEls() {
    if (cDrawer) return;
    cDrawer = document.getElementById("cart-drawer");
    cBackdrop = document.getElementById("cart-drawer-backdrop");
    cBody = document.getElementById("cart-drawer-body");
    cFooter = document.getElementById("cart-drawer-footer");
    cSubtotal = document.getElementById("cart-drawer-subtotal");
    cStatus = document.getElementById("cart-drawer-status");
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function escapeAttr(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function renderCart(cart) {
    getCartEls();
    if (!cBody) return;

    if (!cart || !cart.items || cart.items.length === 0) {
      cBody.innerHTML = '<p class="cart-drawer__empty">Your cart is empty</p>';
      if (cFooter) cFooter.style.display = "none";
      if (cStatus) cStatus.textContent = "Cart is empty.";
      cBody.onclick = null;
      return;
    }

    var html = "";
    cart.items.forEach(function (item, idx) {
      var line = item.line != null ? item.line : idx + 1;
      var img = item.image || "";
      var title = item.product_title || item.title || "";
      var variant =
        item.variant_title && item.variant_title !== "Default Title"
          ? item.variant_title
          : "";
      html += '<div class="cart-drawer__item">';
      if (img) {
        html +=
          '<img class="cart-drawer__item-image" src="' +
          img +
          '" alt="" width="80" height="80" loading="lazy">';
      }
      html += '<div class="cart-drawer__item-details">';
      html +=
        '<a class="cart-drawer__item-title" href="' +
        (item.url || "#") +
        '">' +
        title +
        "</a>";
      if (variant) {
        html += '<p class="cart-drawer__item-variant">' + variant + "</p>";
      }
      html +=
        '<p class="cart-drawer__item-price">' +
        formatMoney(item.final_line_price) +
        "</p>";
      html += '<div class="cart-drawer__item-qty">';
      html +=
        '<button type="button" aria-label="Decrease quantity for ' +
        escapeAttr(title) +
        '" data-cart-line="' +
        line +
        '" data-cart-delta="-1">\u2212</button>';
      html += "<span>" + item.quantity + "</span>";
      html +=
        '<button type="button" aria-label="Increase quantity for ' +
        escapeAttr(title) +
        '" data-cart-line="' +
        line +
        '" data-cart-delta="1">+</button>';
      html += "</div>";
      html +=
        '<button type="button" class="cart-drawer__item-remove" aria-label="Remove ' +
        escapeAttr(title) +
        ' from cart" data-cart-line="' +
        line +
        '">Remove</button>';
      html += "</div></div>";
    });

    cBody.innerHTML = html;
    if (cFooter) cFooter.style.display = "block";
    if (cSubtotal) cSubtotal.textContent = formatMoney(cart.total_price);
    if (cStatus) {
      cStatus.textContent =
        "Cart updated. " +
        cart.item_count +
        " " +
        (cart.item_count === 1 ? "item" : "items") +
        " in cart.";
    }

    cBody.onclick = function (ev) {
      var rm = ev.target.closest(".cart-drawer__item-remove");
      if (rm) {
        var lineRm = parseInt(rm.getAttribute("data-cart-line"), 10);
        if (cStatus) cStatus.textContent = "Removing item from cart.";
        if (!isNaN(lineRm)) NS.Cart.changeQty(lineRm, 0);
        return;
      }
      var b = ev.target.closest("[data-cart-delta]");
      if (!b) return;
      var line = parseInt(b.getAttribute("data-cart-line"), 10);
      var delta = parseInt(b.getAttribute("data-cart-delta"), 10);
      var wrap = b.parentElement;
      var span = wrap ? wrap.querySelector("span") : null;
      var qty = span ? parseInt(span.textContent, 10) || 0 : 0;
      if (!isNaN(line) && !isNaN(delta)) {
        if (cStatus) cStatus.textContent = "Updating cart quantity.";
        NS.Cart.changeQty(line, qty + delta);
      }
    };
  }

  NS.Cart = {
    open: function () {
      getCartEls();
      if (!cDrawer) return;
      NS.Cart.refresh();
      isCartOpen = true;
      cDrawer.classList.add("is-open");
      openDialog(cDrawer, { focusSelector: ".cart-drawer__close" });
    },
    close: function () {
      getCartEls();
      if (!cDrawer) return;
      isCartOpen = false;
      cDrawer.classList.remove("is-open");
      closeDialog(cDrawer);
    },
    refresh: function () {
      fetch("/cart.js")
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          renderCart(cart);
          NS.Cart.updateCount(cart.item_count);
        })
        .catch(function () {
          if (cStatus) cStatus.textContent = "Could not load cart.";
          if (cBody) {
            cBody.innerHTML =
              '<p class="cart-drawer__empty">Could not load cart.</p>';
          }
        });
    },
    changeQty: function (line, quantity) {
      fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ line: line, quantity: quantity }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          renderCart(cart);
          NS.Cart.updateCount(cart.item_count);
        })
        .catch(function () {
          if (cStatus) cStatus.textContent = "Could not update cart. Refreshing cart.";
          NS.Cart.refresh();
        });
    },
    updateCount: function (n) {
      var count = n != null ? n : 0;
      var el = document.querySelector(".site-nav__cart-count");
      var btn = document.querySelector(".site-nav__cart");
      if (el) el.textContent = "(" + count + ")";
      if (btn) {
        btn.setAttribute(
          "aria-label",
          "Cart (" + count + " " + (count === 1 ? "item" : "items") + ")"
        );
      }
    },
  };

  // --- Signup modal ---
  var sModal, sBackdrop;
  var isSignupOpen = false;

  function getSignupEls() {
    if (sModal) return;
    sModal = document.getElementById("signup-modal");
    sBackdrop = document.getElementById("signup-modal-backdrop");
  }

  NS.Signup = {
    open: function () {
      getSignupEls();
      if (!sModal) return;
      if (isGameOpen || isCartOpen || isVideoOpen) return;
      isSignupOpen = true;
      sModal.classList.add("is-open");
      openDialog(sModal, { focusSelector: "#signup-email" });
    },
    close: function () {
      getSignupEls();
      if (!sModal) return;
      isSignupOpen = false;
      sModal.classList.remove("is-open");
      closeDialog(sModal);
      try {
        localStorage.setItem(SIGNUP_STORAGE_KEY, "1");
      } catch (e) {}
    },
  };

  NS.openModal = function (url) {
    if (url) NS.openVideo(url);
  };
  NS.closeModal = function () {
    NS.closeVideo();
  };

  // --- Video modal ---
  var vModal, vBackdrop, vCloseBtn, vContainer, vIframe;
  var isVideoOpen = false;
  var videoCloseTimer = null;

  function getVideoEls() {
    if (vModal) return;
    vModal = document.getElementById("video-modal");
    vBackdrop = document.getElementById("video-modal-backdrop");
    vCloseBtn = document.getElementById("video-modal-close");
    vContainer = document.getElementById("video-modal-container");
    vIframe = document.getElementById("video-iframe");
  }

  NS.openVideo = function (url) {
    getVideoEls();
    if (!vModal || !vIframe || isVideoOpen) return;

    if (videoCloseTimer) {
      clearTimeout(videoCloseTimer);
      videoCloseTimer = null;
    }

    vIframe.src = url;
    document.body.classList.add("is-video-mode");
    vModal.classList.add("is-open");
    isVideoOpen = true;
    openDialog(vModal, { focusSelector: "#video-modal-close" });
  };

  NS.closeVideo = function () {
    getVideoEls();
    if (!vModal || !isVideoOpen) return;
    isVideoOpen = false;

    vModal.classList.remove("is-open");
    document.body.classList.remove("is-video-mode");
    closeDialog(vModal);

    videoCloseTimer = setTimeout(function () {
      if (vIframe) vIframe.src = "";
      videoCloseTimer = null;
      if (NS.resumeCarouselVideos) NS.resumeCarouselVideos();
    }, VIDEO_ANIM_MS);
  };

  // --- DOMContentLoaded: backdrops, signup form, auto-open ---
  document.addEventListener("DOMContentLoaded", function () {
    getGameEls();
    if (gBackdrop) {
      gBackdrop.addEventListener("click", function () {
        NS.closeGame();
      });
    }

    getCartEls();
    if (cBackdrop) {
      cBackdrop.addEventListener("click", function () {
        NS.Cart.close();
      });
    }

    getSignupEls();
    if (sBackdrop) {
      sBackdrop.addEventListener("click", function () {
        NS.Signup.close();
      });
    }

    getVideoEls();
    if (vBackdrop) {
      vBackdrop.addEventListener("click", function () {
        NS.closeVideo();
      });
    }

    var signupForm = document.getElementById("signup-modal-form");
    var signupSuccess = document.getElementById("signup-success");
    var signupBtn = document.getElementById("signup-submit-btn");

    /**
     * Shopify newsletter POST often returns 3xx to ?customer_posted=true.
     * redirect: manual — 3xx has empty body; redirect: follow — follow-up GET
     * can be non-2xx even when the customer was created. Treat 3xx as success
     * unless Location points at a bot challenge.
     */
    function newsletterResponseLooksOk(r, text) {
      var t = text || "";
      var u = (r && r.url) || "";
      if (r && r.ok) return true;
      if (u.indexOf("customer_posted=true") !== -1) return true;
      if (t.indexOf("customer_posted") !== -1) return true;
      if (r && t && /form-message--error|form__message--error|class="(field|form)-error/i.test(t))
        return false;
      return false;
    }

    function finishNewsletterFetch(r) {
      var s = r.status;
      if (s >= 301 && s < 400) {
        var loc = "";
        try {
          loc = r.headers.get("Location") || "";
        } catch (e) {}
        if (loc.indexOf("challenge") !== -1) {
          return r.text().then(function (text) {
            if (newsletterResponseLooksOk(r, text)) return;
            throw new Error("signup failed");
          });
        }
        return Promise.resolve();
      }
      return r.text().then(function (text) {
        if (newsletterResponseLooksOk(r, text)) return;
        throw new Error("signup failed");
      });
    }

    if (signupForm) {
      signupForm.addEventListener("submit", function (e) {
        e.preventDefault();
        if (signupBtn) {
          signupBtn.disabled = true;
          signupBtn.textContent = "Sending...";
        }

        var fd = new FormData(signupForm);
        var action = signupForm.action || "/contact";

        fetch(action, {
          method: "POST",
          body: fd,
          credentials: "same-origin",
          redirect: "manual",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        })
          .then(finishNewsletterFetch)
          .then(function () {
            if (signupSuccess) {
              signupSuccess.textContent = "You're in. Thanks for subscribing.";
            }
            if (signupBtn) {
              signupBtn.disabled = true;
              signupBtn.textContent = "Signed up";
            }
            try {
              localStorage.setItem(SIGNUP_STORAGE_KEY, "1");
            } catch (err) {}
            setTimeout(function () {
              NS.Signup.close();
            }, 1200);
          })
          .catch(function () {
            if (signupSuccess) {
              signupSuccess.textContent =
                "Could not complete signup. Please check your email address and try again.";
            }
            if (signupBtn) {
              signupBtn.disabled = false;
              signupBtn.textContent = "Sign Up";
            }
          });
      });
    }

    var footerThanks = document.getElementById("footer-newsletter-thanks");
    if (footerThanks) {
      setTimeout(function () {
        footerThanks.setAttribute("hidden", "hidden");
        footerThanks.style.display = "none";
      }, 5000);
    }

    try {
      if (!localStorage.getItem(SIGNUP_STORAGE_KEY)) {
        setTimeout(function () {
          if (
            !localStorage.getItem(SIGNUP_STORAGE_KEY) &&
            !isGameOpen &&
            !isCartOpen &&
            !isVideoOpen
          ) {
            NS.Signup.open();
          }
        }, SIGNUP_AUTO_MS);
      }
    } catch (e) {}

    fetch("/cart.js")
      .then(function (r) {
        return r.json();
      })
      .then(function (c) {
        NS.Cart.updateCount(c.item_count);
      })
      .catch(function () {});
  });

  document.addEventListener("keydown", function (e) {
    maintainDialogFocus(e);
    if (e.key !== "Escape") return;
    if (!isGameOpen && !isCartOpen && !isSignupOpen && !isVideoOpen) return;
    e.stopPropagation();
    if (isGameOpen) NS.closeGame();
    else if (isCartOpen) NS.Cart.close();
    else if (isSignupOpen) NS.Signup.close();
    else if (isVideoOpen) NS.closeVideo();
  });
})();
